import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import axios, { AxiosError } from 'axios';
import { ComponentType, useEffect, useState, useRef } from 'react';
import { TokenService } from '../../services/secureTokenService';
import { authReadyGate, AuthPhase } from '../../services/authReadyGate';
import { resetAuthFailureState, axiosClient } from '../../api/axiosClient';
import { AppDispatch } from '../../app/store';
import { PersonalUser } from '../../interfaces/account';
import { useDispatch } from 'react-redux';
import { AppLoadingScreen } from '../AppLoadingScreen/AppLoadingScreen';
import { trackException, trackEvent } from '../../utils/monitoring';
import { loadingSteps, setLoadingStep } from '../../app/Slices/appLoadingSlice';
import {
  fetchAllAccounts,
  fetchInitialData,
} from '../../app/ActionTypes/teamActionTypes';
import {
  getStoredReferrerData,
  buildAuth0UTMParams,
} from '../../utils/utmTracking';
import { isNetworkError } from '../../utils/errorHandling';
import { trackGTMSignupConversion } from '../../utils/gtm';

interface AuthenticationGuardProps {
  component: ComponentType;
}

/**
 * Create a raw axios instance for the health check
 * This bypasses the auth gate since we ARE the initialization
 */
const createHealthCheckClient = () => {
  return axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const AuthenticationGuard = ({
  component,
}: AuthenticationGuardProps) => {
  const {
    getAccessTokenSilently,
    getIdTokenClaims,
    isAuthenticated,
    isLoading,
    error: auth0Error,
    loginWithRedirect,
  } = useAuth0();
  const [isAppLoaded, setIsAppLoaded] = useState(false);
  const [authError, setAuthError] = useState<Error | null>(null);
  const isInitializingRef = useRef(false);
  const dispatch = useDispatch<AppDispatch>();

  /**
   * Helper to update auth phase with logging
   */
  const setPhase = (phase: AuthPhase) => {
    authReadyGate.setPhase(phase);
  };

  /**
   * Handle auth failure - mark gate as failed and redirect to login
   */
  const handleAuthFailure = (reason: string) => {
    console.error(`[AuthenticationGuard] Auth failed: ${reason}`);
    authReadyGate.markFailed(reason);
    TokenService.clearTokens(reason);

    // Get any stored UTM data to pass to Auth0 for tracking
    const signupData = getStoredReferrerData();

    loginWithRedirect({
      authorizationParams: {
        prompt: 'login',
        // Pass UTM and referrer data to Auth0 for tracking in Actions
        ...buildAuth0UTMParams(signupData),
      },
    });
  };

  useEffect(() => {
    const initializeAuth = async () => {
      // Prevent multiple simultaneous initialization attempts using ref
      // This prevents race conditions where state updates might not be synchronized
      if (isInitializingRef.current) {
        console.debug(
          '[AuthenticationGuard] Already initializing, skipping duplicate attempt'
        );
        return;
      }

      isInitializingRef.current = true;

      // Reset any stale auth failure state from previous sessions
      resetAuthFailureState();

      // Start initialization phase - this blocks all API calls via axiosClient
      setPhase('initializing');

      try {
        console.debug('[AuthenticationGuard] Step 1: Getting Auth0 tokens');

        // SECURITY: On page refresh, force a token refresh to validate the user is still active
        // This ensures blocked/deleted users are detected immediately, not just when tokens expire.
        // Without this, a user blocked in Auth0 would still have access until their cached token expires.
        let accessToken: string;
        let idToken: Awaited<ReturnType<typeof getIdTokenClaims>>;

        try {
          // First attempt: Force refresh to validate user status with Auth0
          // This will fail if the user has been blocked/deleted in Auth0
          [accessToken, idToken] = await Promise.all([
            getAccessTokenSilently({ cacheMode: 'off' }),
            getIdTokenClaims(),
          ]);
          console.debug(
            '[AuthenticationGuard] Token refresh succeeded - user is active'
          );
        } catch (tokenError) {
          console.warn(
            '[AuthenticationGuard] Token refresh failed:',
            tokenError
          );

          // Check if this is a user-blocked error from Auth0
          const errorMessage = (tokenError as Error).message?.toLowerCase();
          const isUserBlocked =
            errorMessage?.includes('blocked') ||
            errorMessage?.includes('user is blocked') ||
            errorMessage?.includes('access_denied');

          if (isUserBlocked) {
            console.error(
              '[AuthenticationGuard] User appears to be blocked in Auth0'
            );
            trackException(tokenError as Error, {
              context: 'auth_user_blocked',
            });
            handleAuthFailure('user_blocked');
            return;
          }

          // For other errors (network issues, etc.), try with cache as fallback
          // This allows the app to work offline or during Auth0 outages
          try {
            console.debug(
              '[AuthenticationGuard] Falling back to cached tokens'
            );
            [accessToken, idToken] = await Promise.all([
              getAccessTokenSilently({ cacheMode: 'on' }),
              getIdTokenClaims(),
            ]);
            console.debug('[AuthenticationGuard] Using cached tokens');
          } catch (cacheError) {
            console.error(
              '[AuthenticationGuard] Both token retrieval attempts failed:',
              cacheError
            );

            // Track the failure for monitoring
            trackException(cacheError as Error, {
              context: 'auth_token_retrieval',
              first_error: (tokenError as Error).message,
              second_error: (cacheError as Error).message,
            });

            // Both attempts failed - redirect to login
            handleAuthFailure('token_retrieval_failed');
            return;
          }
        }

        console.debug('Successfully retrieved Auth0 tokens:', {
          accessToken: !!accessToken,
          idToken: !!idToken,
        });

        if (!accessToken || !idToken) {
          handleAuthFailure('missing_tokens');
          return;
        }

        console.debug('[AuthenticationGuard] Tokens retrieved:', {
          hasAccessToken: !!accessToken,
          accessTokenLength: accessToken?.length,
          hasIdToken: !!idToken,
          idTokenSub: idToken?.sub,
          idTokenExp: idToken?.exp,
          idTokenIat: idToken?.iat,
          currentTime: Date.now() / 1000,
        });

        dispatch(setLoadingStep(loadingSteps.USER_LOADING));

        // CRITICAL: Set tokens in TokenService BEFORE any API calls
        console.debug('[AuthenticationGuard] Setting tokens in TokenService');
        TokenService.setTokens(accessToken, idToken);
        console.debug('[AuthenticationGuard] Tokens set successfully');

        // Move to health check phase
        setPhase('health_check');

        // HEALTH CHECK: Make ONE test request to validate auth before proceeding
        // This prevents cascading errors by failing fast if auth is invalid
        // Use a raw axios instance that bypasses the auth gate
        const healthCheckClient = createHealthCheckClient();

        try {
          console.debug(
            '[AuthenticationGuard] Making health check request to /accounts/users/me'
          );
          const healthCheckResponse = await healthCheckClient.get(
            '/v1/accounts/users/me',
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          console.debug(
            '[AuthenticationGuard] Health check succeeded:',
            healthCheckResponse.status
          );
        } catch (healthCheckError) {
          console.error(
            '[AuthenticationGuard] Health check failed:',
            healthCheckError
          );

          // Check if it's an axios error with response
          const axiosError = healthCheckError as AxiosError;

          // Check for network errors
          if (isNetworkError(axiosError)) {
            const message =
              (axiosError.response?.data as { message?: string })?.message ||
              axiosError.message;
            authReadyGate.markFailed('network_error');
            setAuthError(new Error(message || 'Unable to connect to server'));
            return;
          }

          // Check for auth errors (401 or 429 from rate limiting)
          const statusCode = axiosError.response?.status || 0;
          if (statusCode === 401 || statusCode === 429) {
            handleAuthFailure(
              statusCode === 401 ? 'unauthorized' : 'rate_limited'
            );
            return;
          }
          // For other errors, proceed to load the app - errors will be shown via notifications
        }

        // Move to loading data phase
        setPhase('loading_data');

        // CRITICAL: Mark auth as ready BEFORE data fetches
        // The health check passed, so tokens are valid. Open the gate so
        // fetchAllAccounts() and other API calls can proceed via axiosClient.
        // If we wait until AFTER these fetches, we get a deadlock:
        // - fetchAllAccounts() uses axiosClient
        // - axiosClient waits for authReadyGate.waitUntilReady()
        // - But markReady() would only be called after fetchAllAccounts() completes
        // - DEADLOCK!
        console.debug(
          '[AuthenticationGuard] Step 3: Health check passed, opening auth gate'
        );
        authReadyGate.markReady();

        console.debug(
          '[AuthenticationGuard] Step 4: Auth validated, fetching account data'
        );
        dispatch(setLoadingStep(loadingSteps.USER_LOADING));

        // Auth is valid - now fetch accounts and other data
        const accountsResult = await dispatch(fetchAllAccounts());

        if (accountsResult.type.endsWith('/rejected')) {
          const payload = accountsResult.payload as {
            errorCode?: string;
            statusCode?: number;
            message?: string;
          };

          console.error('[AuthenticationGuard] Account fetch failed:', payload);

          // Only show error for network errors
          if (isNetworkError(payload)) {
            authReadyGate.markFailed('network_error');
            setAuthError(
              new Error(payload.message || 'Unable to connect to server')
            );
            return;
          }

          // For non-network errors, continue - notifications will be shown
        }

        console.debug(
          '[AuthenticationGuard] Step 5: Fetching remaining initial data'
        );
        dispatch(setLoadingStep(loadingSteps.ORG_LOADING));

        // Fetch the rest of the initial data
        const dataResult = await dispatch(fetchInitialData());

        if (dataResult.type.endsWith('/rejected')) {
          const payload = dataResult.payload as {
            errorCode?: string;
            statusCode?: number;
            message?: string;
          };

          console.error(
            '[AuthenticationGuard] Initial data fetch failed:',
            payload
          );

          // Only show error for network errors - other errors will show notifications
          if (isNetworkError(payload)) {
            authReadyGate.markFailed('network_error');
            setAuthError(
              new Error(payload.message || 'Unable to connect to server')
            );
            return;
          }

          // For non-network errors, continue - the app can still function
          // and notifications will be shown for specific failures
        }

        console.debug('[AuthenticationGuard] Step 6: Initialization complete');

        // Track every successful login/authentication
        trackEvent('User Login', {
          auth_method: 'auth0',
        });

        // Track successful signup completion for OAuth flows (e.g., Google) that skip email verification
        // Check if we have stored signup attribution data - if yes, this is completing a signup journey
        const signupData = getStoredReferrerData();
        const hasSignupSessionIds =
          localStorage.getItem('signup_session_id') ||
          localStorage.getItem('signup_correlation_id');

        if (signupData && hasSignupSessionIds) {
          console.log(
            '[AuthenticationGuard] Detected OAuth signup completion - tracking event'
          );
          trackEvent('OAuth Signup Completed', {
            hasSignupAttribution: true,
            ...signupData,
          });

          // Clear the stored signup tracking IDs now that we've completed the signup journey tracking
          localStorage.removeItem('signup_session_id');
          localStorage.removeItem('signup_correlation_id');
        }

        // Fire GTM conversion event for Google Ads attribution on new signups.
        // The backend sets signup_conversion_pending=true on account creation.
        // This is more robust than localStorage because it survives cross-browser
        // and cross-device email verification flows.
        const personalAccount = (
          accountsResult.payload as {
            personalAccount?: PersonalUser;
          }
        )?.personalAccount;
        if (
          personalAccount?.signup_conversion_pending &&
          !sessionStorage.getItem('signup_conversion_fired')
        ) {
          trackGTMSignupConversion();
          // Prevent duplicate GTM fires within the same session (e.g. if the
          // PATCH below fails because the endpoint isn't deployed yet).
          sessionStorage.setItem('signup_conversion_fired', 'true');
          // Clear the flag on the backend so it doesn't fire on the next session.
          // Silently swallow errors so a missing endpoint doesn't break login.
          axiosClient
            .patch('/accounts/users/me/signup-conversion-completed')
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            .catch(() => {});
        }

        // Clear UTM data now that signup is complete. Auth0 already received UTM
        // via authorizationParams, and initial API requests have been made.
        // Keeping it would add unnecessary x-utm-* headers to every future request.
        if (signupData) {
          localStorage.removeItem('signup_referrer_data');
        }

        setIsAppLoaded(true);
      } catch (error) {
        // Catch any unexpected errors
        const errorObj = error as {
          message?: string;
          errorCode?: string;
          statusCode?: number;
        };
        console.log(
          '[AuthenticationGuard] Caught unexpected error in initializeAuth:',
          {
            message: errorObj.message,
            errorCode: errorObj.errorCode,
            statusCode: errorObj.statusCode,
          }
        );

        // Mark auth as failed so waiting requests don't hang forever
        authReadyGate.markFailed('unexpected_error');

        // Track signup initialization failure with UTM context (without PII)
        const signupData = getStoredReferrerData();
        if (signupData) {
          trackEvent('Signup Initialization Failed', {
            error_message: errorObj.message || 'Unknown error',
            error_code: errorObj.errorCode,
            status_code: errorObj.statusCode,
            ...signupData,
          });
        }

        trackException(error as Error, {
          context: 'signup_initialization',
          ...(signupData || {}),
        });

        // Set error state to show blocking screen
        setAuthError(error as Error);
      } finally {
        // Always reset the initialization flag, even on error
        isInitializingRef.current = false;
      }
    };

    if (isAuthenticated) {
      dispatch(setLoadingStep(loadingSteps.TOKEN_LOADING));
      initializeAuth();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (auth0Error) {
      console.log('[AuthenticationGuard] Auth0 error detected:', auth0Error);
      trackException(new Error('Auth0 error: ' + auth0Error));
      authReadyGate.markFailed('auth0_error');
      setAuthError(auth0Error);
    }
  }, [auth0Error]);

  // Check if Auth0 is stuck loading due to network issues
  useEffect(() => {
    if (isLoading) {
      // Set a timeout to detect if Auth0 is stuck due to network issues
      const timeout = setTimeout(() => {
        console.log(
          '[AuthenticationGuard] Auth0 loading timeout - possible network issue'
        );
        authReadyGate.markFailed('auth0_timeout');
        setAuthError(
          new Error(
            'Unable to connect to authentication server. Please check your internet connection.'
          )
        );
      }, 10000); // 10 second timeout

      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  if (authError) {
    return <AppLoadingScreen error={authError} />;
  }

  const Component = withAuthenticationRequired(component, {
    onRedirecting: () => {
      return <AppLoadingScreen />;
    },
  });

  // Show loading screen if:
  // 1. Auth0 is still loading
  // 2. User is authenticated but app data hasn't loaded yet
  if (isLoading || (!isAppLoaded && isAuthenticated)) {
    return <AppLoadingScreen />;
  }

  return <Component />;
};
